// import { getDateISOString } from "./utils.js";
import { type ScaleSequential, scaleSequential } from "d3-scale";
import { interpolateWarm } from "d3-scale-chromatic";
import type { Marker } from "leaflet";
// import { titles } from "./constants.js";
import { dataFetcher } from "./dataFetching.js";
import type { Stations } from "./global.d";
export class MapHandler {
	mapDivEl: HTMLElement;
	dataFetcher: dataFetcher;
	map!: L.Map;
	timestampedData: {
		[key: string]: { [key: string]: { windSpeed?: number; windDir?: number } };
	};
	startDateTime?: Date;
	endDateTime: Date;
	minScaleTemp: number;
	maxScaleTemp: number;
	scaleSteps: number;
	colorScale: ScaleSequential<string, never>;
	rangeSliderDivEl?: HTMLElement | null;
	rangeSliderEl?: HTMLInputElement | null;
	differenceInMilliseconds: number;
	rangeSliderLabelEl?: HTMLElement | null;
	timeInterval: number;
	intervalId?: NodeJS.Timeout;
	mapDivId: string;
	group: string;
	constructor(mapDivId: string, group: string) {
		this.mapDivId = mapDivId;
		this.group = group;
		this.mapDivEl = document.getElementById(mapDivId) as HTMLElement;
		this.dataFetcher = new dataFetcher();
		this.timestampedData = {};
		this.minScaleTemp = -40;
		this.maxScaleTemp = 40;
		this.scaleSteps = 8;
		this.colorScale = scaleSequential(interpolateWarm).domain([
			this.minScaleTemp,
			this.maxScaleTemp,
		]);
		this.endDateTime = new Date();
		this.differenceInMilliseconds = 0;
		this.timeInterval = 10;
		this.rangeSliderDivEl = undefined;
		this.rangeSliderEl = undefined;
		this.rangeSliderLabelEl = undefined;
		this.intervalId = undefined;
		this.startDateTime = undefined;
		this.map = undefined as unknown as L.Map;
		if (group === "New Zealand Stations") this.map = this.setupNZMap();
		else if (group === "Antarctic Stations")
			this.map = this.setupAntarcticMap();
		if (!this.map) {
			console.error(`No Map was created for the group ${group}`);
			return;
		}
		if (!this.mapDivEl) {
			console.error(`No Map div element with the id ${mapDivId}`);
			return;
		}
		this.initMap();
	}

	showMap(): void {
		if (this.mapDivEl && this.mapDivEl.style.display !== "block") {
			if (this.rangeSliderDivEl) this.rangeSliderDivEl.style.display = "block";
			this.mapDivEl.style.display = "block";
			const newDateTime = this.getSliderTime(this.rangeSliderEl?.value);

			this.updateMap(newDateTime);
		}
	}

	hideMap(): void {
		if (this.rangeSliderDivEl) this.rangeSliderDivEl.style.display = "none";

		this.mapDivEl.style.display = "none";
	}

	initMap(): void {
		const resizeObserver = new ResizeObserver(() => {
			if (this.map) this.map.invalidateSize();
		});

		resizeObserver.observe(this.mapDivEl);

		this.addLegend();

		this.startDateTime = this.getStartDateTime();
		this.endDateTime = this.getEndDateTime();

		this.differenceInMilliseconds = Math.abs(
			+this.startDateTime - +this.endDateTime,
		);

		this.createTimestampedData(stationMap, {
			start: this.startDateTime,
			end: this.endDateTime,
		})
			.then(() => {
				const time = this.getSliderTime(this.rangeSliderEl?.value);
				this.updateMap(time);
			})
			.catch((e) => console.error(e));
		this.updateRangeSlider();
		this.rangeSliderEl?.addEventListener("input", (event) => {
			const target = event.target as HTMLInputElement;
			if (target) {
				const newDateTime = this.getSliderTime(target.value);
				this.updateMap(newDateTime);
				if (this.rangeSliderLabelEl && newDateTime)
					this.rangeSliderLabelEl.textContent = `Selected Date and Time: ${newDateTime.toString()}`;
			}
		});
		this.startPeriodicFetching();
	}

	getStartDateTime(): Date {
		const startDateTime = new Date(new Date().setMinutes(0, 0, 0));
		startDateTime.setDate(startDateTime.getDate() - 7);
		return startDateTime;
	}

	getEndDateTime(): Date {
		const endDateTime = new Date();

		// Round up to the nearest full interval (hour)
		const minutes = endDateTime.getMinutes();
		const roundedMinutes =
			Math.ceil(minutes / this.timeInterval) * this.timeInterval;
		endDateTime.setMinutes(roundedMinutes % 60, 0, 0);
		return endDateTime;
	}

	startPeriodicFetching(): void {
		if (!this.intervalId) {
			this.intervalId = setInterval(() => {
				if (!this.startDateTime) return;
				this.startDateTime = this.getStartDateTime();
				this.endDateTime = this.getEndDateTime();
				this.differenceInMilliseconds = Math.abs(
					+this.startDateTime - +this.endDateTime,
				);

				this.createTimestampedData(stationMap, {
					start: this.startDateTime,
					end: this.endDateTime,
				}).catch((e) => console.error(e));
				this.updateRangeSlider();
			}, this.timeInterval * 60000);
		}
	}

	stopPeriodicFetching(): void {
		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = undefined;
		}
	}

	getSliderTime(value?: string): Date | undefined {
		if (!value || !this.startDateTime) return;
		const sliderValue = +value;
		const date = new Date(this.startDateTime);

		// Add the specified number of minutes
		date.setMinutes(date.getMinutes() + sliderValue * this.timeInterval);
		// Return the updated timestamp in ISO format
		return new Date(date);
	}

	updateRangeSlider(): void {
		// Convert the difference from milliseconds to hours
		if (!this.rangeSliderEl || !this.rangeSliderLabelEl) return;
		const range =
			this.differenceInMilliseconds / (this.timeInterval * 1000 * 60);

		this.rangeSliderEl?.setAttribute("max", range.toString());
		this.rangeSliderEl.value = range.toString();
		this.rangeSliderLabelEl.textContent = `Selected Date and Time: ${this.endDateTime.toLocaleDateString()}`;
	}

	setupAntarcticMap(): L.Map {
		// one hour interval
		this.timeInterval = 60; //in minutes
		this.rangeSliderEl = document.getElementById(
			"timeSliderAnt",
		) as HTMLInputElement;
		this.rangeSliderLabelEl = document.getElementById("timeSliderAntLabel");
		this.rangeSliderDivEl = document.getElementById("timeSliderAntDiv");

		const proj = "EPSG:3031";
		const proj4 =
			"+proj=stere +lat_0=-90 +lat_ts=-71 +lon_0=0 +k=1 +x_0=0 +y_0=0 +ellps=WGS84 +datum=WGS84 +units=m +no_defs";
		// @ts-expect-error proj exists
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		const crs = new L.Proj.CRS(proj, proj4, {
			resolutions: [8192, 4096, 2048, 1024, 512, 256, 128, 64],
			origin: [-4194304, 4194304],
			bounds: L.bounds([-4194304, -4194304], [4194304, 4194304]),
		});

		const map = L.map(this.mapDivId, {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			crs: crs,
			center: [-76, 170],
			zoom: 2,
			maxZoom: 5,
		});

		L.tileLayer(
			"https://gitc.earthdata.nasa.gov/wmts/epsg3031/best/MODIS_Aqua_CorrectedReflectance_TrueColor/default/2022-10-01/250m/{z}/{y}/{x}.jpg",
			{
				tileSize: 512,
				attribution: "<a href='https://earthdata.nasa.gov/gibs'> NASA </a>",
			},
		).addTo(map);

		L.tileLayer
			.wms("https://geos.polarview.aq/geoserver/wms", {
				layers: "polarview:coastS10",
				format: "image/png",
				transparent: true,
				attribution:
					"<a href='https://www.polarview.aq/antarctic'>Polarview</a>",
				tileSize: 512,
			})
			.addTo(map);
		//@ts-expect-error imports package in base.html
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		L.graticule({
			intervalLat: 10,
			intervalLng: 30,
			latBounds: [-90, -55],
			centerLonLabels: true,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		}).addTo(map);
		return map;
	}
	setupNZMap(): L.Map {
		this.timeInterval = 10; //in minutes
		this.rangeSliderEl = document.getElementById(
			"timeSliderNZ",
		) as HTMLInputElement;
		this.rangeSliderLabelEl = document.getElementById("timeSliderNZLabel");
		this.rangeSliderDivEl = document.getElementById("timeSliderNZDiv");

		const proj = "EPSG:2193";
		const proj4 =
			"+proj=tmerc +lat_0=0 +lon_0=173 +k=0.9996 +x_0=1600000 +y_0=10000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs";
		//@ts-expect-error Proj exists
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		const crs = new L.Proj.CRS(proj, proj4, {
			origin: [-1000000, 10000000],
			resolutions: [
				8960, 4480, 2240, 1120, 560, 280, 140, 70, 28, 14, 7, 2.8, 1.4, 0.7,
				0.28, 0.14, 0.07,
			],
		});
		const map = new L.Map(this.mapDivId, {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			crs: crs,
			//@ts-expect-error use for NZ maps
			continuousWorld: true,
			worldCopyJump: false,
			zoomControl: false,
		});

		const APIKey = "d01egend5f8bk5s0jvvebpy9v9x";

		// LDS tile URL
		const urlTemplate =
			"https://basemaps.linz.govt.nz/v1/tiles/aerial/EPSG:2193/{z}/{x}/{y}.webp?api=" +
			APIKey;

		const settings = {
			maxZoom: 17,
			continuousWorld: true,
			attribution: "<a href='https://www.linz.govt.nz/'> LINZ </a>",
		};

		const tilelayer = new L.TileLayer(urlTemplate, settings);
		map.addLayer(tilelayer);

		map.setView([-43, 172], 5);
		//@ts-expect-error imports package in base.html
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		L.graticule({
			intervalLat: 5,
			intervalLng: 5,
			latBounds: [-50, 30],
			lngBounds: [160, 180],
			centerLonLabels: true,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		}).addTo(map);
		return map;
	}

	addLegend(): void {
		// Define the range and number of steps

		// Calculate the step size
		const stepSize = (this.maxScaleTemp - this.minScaleTemp) / this.scaleSteps;

		// Generate the grades array dynamically
		const grades: number[] = [];
		for (let i = this.scaleSteps - 1; i >= 0; i--) {
			grades.push(this.minScaleTemp + i * stepSize);
		}

		// Create a legend
		//@ts-expect-error is callable
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const legend = L.control({ position: "bottomright" });

		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		legend.onAdd = (): HTMLDivElement => {
			const div = L.DomUtil.create("div", "info legend d-flex flex-column");
			//@ts-expect-error imported as package
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			const xIcon = L.WindBarb.icon({
				mirrorVel: true,
				speed: 30,
				pointRadius: 5,
				strokeLength: 20,
				fillColor: "#FF0000",
			});
			// Add the red circle as the first item
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			div.innerHTML += `<div class="d-flex"><div class="flex-shrink-0">${xIcon.createIcon().innerHTML}</div><div class="flex-grow-1 ms-3"><span>No Data</span></div></div>`;

			for (let i = 0; i < grades.length - 1; i++) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				xIcon.options.fillColor = this.colorScale(grades[i]);
				// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
				div.innerHTML += `<div class="d-flex"><div class="flex-shrink-0">${xIcon.createIcon().innerHTML}</div><div class="flex-grow-1 ms-3"><span>${
					grades[i]
				} to ${grades[i + 1]}</span></div></div>`;
			}
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			const legendWindBarb = xIcon.createShadow();
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			legendWindBarb.children[0].setAttribute("width", 50);
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			legendWindBarb.children[0].setAttribute("height", 30);

			// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			legendWindBarb.children[0].children[0].setAttribute(
				"style",
				"transform:translate(57px,70px) rotate(180deg);",
			);
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			div.innerHTML += `<div class="d-flex align-items-center">${legendWindBarb.innerHTML}<div class="flex-shrink-0"><span><a href="https://commons.wikimedia.org/wiki/Wind_speed" target=_blank>Wind Barb</a></span></div></div>`;
			return div;
		};

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		legend.addTo(this.map);
	}

	addStations(time: string): void {
		if (time in this.timestampedData) {
			for (const [stationId, station] of Object.entries(stationMap)) {
				if (stationId in this.timestampedData[time]) {
					if (station.group === this.group) {
						const windData = this.timestampedData[time][stationId];
						//@ts-expect-error WindBarb is defined by package
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
						const icon = L.WindBarb.icon({
							mirrorVel: +station.lat < 0,
							lat: station.lat,
							deg: ((windData.windDir ?? 0) + +station.lon) % 360,
							speed: (windData.windSpeed ?? 0) * 1.94384, // m/s to knots
							pointRadius: 5,
							strokeLength: 20,
							fillColor: this.colorScale(windData.windSpeed ?? 0),
						});
						const marker = L.marker([+station.lat, +station.lon], {
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							icon: icon,
						}).addTo(this.map);
						this.createPopup(station.name, time, windData, marker);
					}
				} else {
					if (station.group === this.group) {
						//@ts-expect-error WindBarb is defined by package
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
						const xIcon = L.WindBarb.icon({
							pointRadius: 5,
							strokeLength: 20,
							fillColor: "#FF0000",
						});
						L.marker([+station.lat, +station.lon], {
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							icon: xIcon,
						}).addTo(this.map);
					}
				}
			}
		} else {
			for (const station of Object.values(stationMap)) {
				if (station.group === this.group) {
					//@ts-expect-error WindBarb is defined by package
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
					const xIcon = L.WindBarb.icon({
						pointRadius: 5,
						strokeLength: 20,
						fillColor: "#FF0000",
					});
					L.marker([+station.lat, +station.lon], {
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						icon: xIcon,
					}).addTo(this.map);
				}
			}
		}
	}
	async createTimestampedData(
		stationMap: Stations,
		dateRangeText: { start: Date; end: Date },
	): Promise<
		Record<string, Record<string, { windSpeed?: number; windDir?: number }>>
	> {
		const awsData = await this.dataFetcher.fetchData(
			null,
			dateRangeText.start.toISOString(),
			dateRangeText.end.toISOString(),
		);
		this.timestampedData = {};
		for (const [stationId, station] of Object.entries(stationMap)) {
			if (station.group === this.group) {
				const stationData = awsData.data[stationId];
				if (stationData) {
					for (const [tableId, table] of Object.entries(stationData)) {
						// Role-based wind field extraction
						const fieldsArr = awsData.fields?.[tableId];
						if (!fieldsArr) {
							console.error(
								`[createTimestampedData] No field metadata for tableId ${tableId} (station ${stationId})`,
							);
							// Optionally throw if this is a critical error:
							throw new Error(
								`[createTimestampedData] No field metadata for tableId ${tableId} (station ${stationId})`,
							);
						}
						const speedIdx = fieldsArr.findIndex(
							(f) => f.role === "wind_speed",
						);
						const dirIdx = fieldsArr.findIndex(
							(f) => f.role === "wind_direction",
						);
						const timeIdx = fieldsArr.findIndex(
							(f) => f.role === "timestamp" || f.name === "Timestamp",
						);
						if (speedIdx === -1 || dirIdx === -1 || timeIdx === -1) {
							console.error(
								`[createTimestampedData] Wind field index not found for station ${stationId}, table ${tableId}:`,
								{ speedIdx, dirIdx, timeIdx },
							);
							continue;
						}
						const speedArr = table[speedIdx];
						const dirArr = table[dirIdx];
						const timeArr = table[timeIdx];
						if (!speedArr || !dirArr || !timeArr) {
							console.error(
								`[createTimestampedData] Missing wind arrays for station ${stationId}, table ${tableId}:`,
								{ speedArr, dirArr, timeArr },
							);
							continue;
						}
						for (let i = 0; i < timeArr.length; i++) {
							const windSpeedVal = speedArr[i];
							const windDirVal = dirArr[i];
							if (windSpeedVal == null || windDirVal == null) {
								continue;
							}
							const dateTime = new Date(timeArr[i] as string).toISOString();
							if (!this.timestampedData[dateTime])
								this.timestampedData[dateTime] = {};
							this.timestampedData[dateTime][stationId] = {
								windSpeed:
									typeof windSpeedVal === "number"
										? windSpeedVal
										: parseFloat(windSpeedVal as string),
								windDir:
									typeof windDirVal === "number"
										? windDirVal
										: parseFloat(windDirVal as string),
							};
						}
					}
				}
			}
		}
		return this.timestampedData;
	}
	createPopup(
		name: string,
		timeStamp: string,
		data: { windSpeed?: number; windDir?: number },
		marker: Marker<GeoJSON.Point>,
	): void {
		if (data && (data.windSpeed !== undefined || data.windDir !== undefined)) {
			let popupText = `<h4>Latest Data: ${name}</h4><table class="table table-striped table-sm"><tbody>`;
			const dateTime = new Date(timeStamp);
			popupText += `<tr><td>Time</td><td>${dateTime.toString()}</td></tr>`;
			if (data.windSpeed !== undefined)
				popupText += `<tr><td>Wind Speed (m/s)</td><td>${data.windSpeed.toFixed(2)}</td></tr>`;
			if (data.windDir !== undefined)
				popupText += `<tr><td>Wind Direction (°)</td><td>${data.windDir.toFixed(1)}</td></tr>`;
			popupText += "</tbody></table>";
			marker.bindPopup(popupText);
		} else {
			marker.bindPopup(`No data during ${date_range_text}`);
		}
	}
	updateMap(sliderValue: Date | undefined): void {
		if (!sliderValue) return;
		const dateTime = new Date(sliderValue).toISOString();
		// Clear existing markers
		this.map.eachLayer((layer) => {
			if (layer instanceof L.Marker) {
				this.map.removeLayer(layer);
			}
		});

		// Add stations for the selected time
		this.addStations(dateTime);
	}
}
